import React, { useState } from 'react';
import Swal from 'sweetalert2';
import api from '../services/api';
import { getSwalTheme } from '../utils/swalTheme';
import { DRIVE_TYPES } from '../constants/vehicles';
import useModalAccessibility from '../hooks/useModalAccessibility';
import './EditShiftModal.css';

const EditShiftModal = ({ shift, onClose, onUpdate, isAdmin = false }) => {
  const modalRef = useModalAccessibility(onClose);
  const apiPath = isAdmin ? '/admin/shifts' : '/shifts';
  const [formData, setFormData] = useState({
    title: shift.title,
    description: shift.description || '',
    shift_type: shift.shift_type,
    garage_address: shift.garage_address || '',
    vehicle_type: shift.vehicle_type,
    drive_type: shift.drive_type || 'standard',
    wav: !!shift.wav,
    medallion_number: shift.medallion_number || '',
    pay_rate: shift.pay_rate || '',
    pay_type: shift.shift_type === 'dov' ? 'weekly' : shift.pay_type,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-set pay_type based on shift_type
    if (name === 'shift_type') {
      const payType = (value === 'dov' || value.startsWith('weekly_')) ? 'weekly' : 'daily';
      setFormData(prev => ({ ...prev, [name]: value, pay_type: payType }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        pay_rate: formData.pay_rate !== '' ? parseFloat(formData.pay_rate) : null,
      };
      await api.put(`${apiPath}/${shift.id}`, submitData);
      onUpdate();
      onClose();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Car Listing Updated!',
        text: 'Your changes have been saved.'
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update car listing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        className="edit-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-shift-title"
      >
        <button
          type="button"
          className="modal-close"
          onClick={onClose}
          aria-label="Close dialog"
        >
          &times;
        </button>

        <div className="modal-header">
          <h2 id="edit-shift-title">Edit Car Listing</h2>
        </div>

        <form onSubmit={handleSubmit} className="edit-shift-form">
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="vehicle_type">Work Type</label>
              <select
                id="vehicle_type"
                name="vehicle_type"
                value={formData.vehicle_type}
                onChange={handleChange}
              >
                <option value="yellow_cab">Yellow Cab</option>
                <option value="green_boro">Green Boro</option>
                <option value="black_car">Black Car</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="drive_type">Drive Type *</label>
              <select
                id="drive_type"
                name="drive_type"
                value={formData.drive_type}
                onChange={handleChange}
              >
                {DRIVE_TYPES.map(dt => (
                  <option key={dt.value} value={dt.value}>{dt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="wav"
                  checked={formData.wav}
                  onChange={handleCheckboxChange}
                />
                WAV
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="medallion_number">Medallion #</label>
            <input
              type="text"
              id="medallion_number"
              name="medallion_number"
              value={formData.medallion_number}
              onChange={handleChange}
              placeholder="e.g., 1A23"
            />
          </div>

          <div className="form-group">
            <label htmlFor="shift_type">Shift Type *</label>
            <select
              id="shift_type"
              name="shift_type"
              value={formData.shift_type}
              onChange={handleChange}
              required
            >
              <option value="">Select shift type</option>
              <option value="dov">DOV (Drive Own Vehicle)</option>
              <option value="weekly_24">Weekly 24hr</option>
              <option value="weekly_day">Weekly Day</option>
              <option value="weekly_night">Weekly Night</option>
              <option value="daily_day">Daily Day</option>
              <option value="daily_night">Daily Night</option>
              <option value="daily_24">Daily 24hr</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="garage_address">Location</label>
            <input
              type="text"
              id="garage_address"
              name="garage_address"
              value={formData.garage_address}
              onChange={handleChange}
              placeholder="e.g., Queens garage, 123 Main St"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pay_rate">Rate ($)</label>
              <input
                type="number"
                id="pay_rate"
                name="pay_rate"
                value={formData.pay_rate}
                onChange={handleChange}
                placeholder="Optional"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>Rate Type</label>
              <div className="pay-type-display">
                {formData.pay_type === 'weekly' ? 'Weekly' : 'Daily'}
                <span className="pay-type-hint">(based on shift type)</span>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditShiftModal;
